import type { MeResponse } from '@nolens/domain';
import { formatRelative } from '@nolens/i18n';
import { cx, Dialog, SiteIcon, useLocale } from '@nolens/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { spaceName } from '../lib/locale';
import { useAllGuides } from '../lib/queries';
import { shellText } from '../i18n/shell';
import { SearchIcon } from './Icons';

/** Procurar guias em todos os espaços da pessoa (título e descrição). Abre com ⌘K / Ctrl+K. */
export function SearchDialog({
  me,
  open,
  onClose,
}: {
  me: MeResponse;
  open: boolean;
  onClose: () => void;
}) {
  const locale = useLocale();
  const t = shellText(locale);
  const guides = useAllGuides({ enabled: open });
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const spaces = useMemo(() => new Map(me.workspaces.map((w) => [w.id, w])), [me.workspaces]);

  // Depois de o <dialog> abrir (ele põe o foco no primeiro botão), o foco vai para a pesquisa.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const needle = q.trim().toLowerCase();
  const results = needle
    ? (guides.data ?? [])
        .filter((g) => (g.title + ' ' + g.description).toLowerCase().includes(needle))
        .slice(0, 20)
    : [];
  const close = () => {
    setQ('');
    setActive(0);
    onClose();
  };
  const go = (id: string) => {
    close();
    navigate(`/g/${id}`);
  };

  return (
    <Dialog open={open} onClose={close} title={t.searchGuides} closeLabel={t.close}>
      <div className="grid gap-3">
        <label className="relative block">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
          <input
            ref={input}
            type="search"
            aria-label={t.searchGuides}
            placeholder={t.searchPlaceholder}
            className="h-11 w-full rounded-[12px] bg-surface-0 pl-10 pr-4 text-[15px] shadow-[inset_0_0_0_1px_var(--border)] outline-none focus:shadow-[inset_0_0_0_2px_var(--accent)]"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter' && results[active]) {
                e.preventDefault();
                go(results[active].id);
              }
            }}
            data-search-input
          />
        </label>
        {!needle ? (
          <p className="px-1 text-sm text-text-3">{t.searchHint}</p>
        ) : guides.isPending ? null : results.length === 0 ? (
          <p className="px-1 text-sm text-text-2">{t.searchNone(q.trim())}</p>
        ) : (
          <ul aria-label={t.searchResults} className="-mx-1 max-h-[50dvh] overflow-y-auto">
            {results.map((g, i) => {
              const ws = spaces.get(g.workspaceId);
              return (
                <li key={g.id}>
                  <Link
                    to={`/g/${g.id}`}
                    onClick={close}
                    onMouseEnter={() => setActive(i)}
                    className={cx(
                      'flex items-center gap-3 rounded-[10px] px-3 py-2.5',
                      i === active && 'bg-surface-2',
                    )}
                  >
                    {g.site ? (
                      <SiteIcon site={g.site} className="size-5" />
                    ) : (
                      <span className="size-5 shrink-0 rounded-[4px] bg-surface-2" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14.5px] font-medium">
                        {g.title || t.untitled}
                      </span>
                      <span className="block truncate text-[12.5px] text-text-3">
                        {ws ? spaceName(ws, locale) : ''} · {formatRelative(locale, g.updatedAt)}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
